<script setup>
import { computed } from 'vue';
import FlexComponent from './FlexComponent.vue';

const props = defineProps({
  flexJson: {
    type: Object,
    required: true,
  },
});

// Render a Flex container (bubble or carousel)
const containerType = computed(() => props.flexJson?.type || 'bubble');
const bubbles = computed(() => {
  if (containerType.value === 'carousel') {
    return props.flexJson?.contents || [];
  }
  return [props.flexJson];
});

// Helper to convert LINE color format
const parseColor = color => {
  if (!color) return undefined;
  // LINE uses #RRGGBBAA format, CSS uses #RRGGBB or rgba
  if (color.length === 9 && color.startsWith('#')) {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    const a = parseInt(color.slice(7, 9), 16) / 255;
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  }
  return color;
};

// Size mapping
const sizeMap = {
  xxs: '10px',
  xs: '12px',
  sm: '14px',
  md: '16px',
  lg: '18px',
  xl: '22px',
  xxl: '28px',
  '3xl': '34px',
  '4xl': '42px',
  '5xl': '52px',
};

const imageSizeMap = {
  xxs: '40px',
  xs: '60px',
  sm: '80px',
  md: '100px',
  lg: '120px',
  xl: '160px',
  xxl: '200px',
  '3xl': '240px',
  '4xl': '280px',
  '5xl': '320px',
  full: '100%',
};

const getSize = (size, isImage = false) => {
  if (isImage) {
    return imageSizeMap[size] || size || '100%';
  }
  return sizeMap[size] || size || '16px';
};

// Weight mapping
const weightMap = {
  regular: '400',
  bold: '700',
};

// Get bubble styles (from bubble.styles or fallback to body background)
const getBubbleStyles = bubble => {
  const styles = {};

  // Check bubble-level styles first (LINE Flex spec)
  if (bubble?.styles?.body?.backgroundColor) {
    styles.backgroundColor = parseColor(bubble.styles.body.backgroundColor);
  }

  // If bubble has background at body level, apply to bubble container
  const body = bubble?.body;
  if (body?.background?.type === 'linearGradient') {
    const bg = body.background;
    const startColor = parseColor(bg.startColor) || '#000';
    const endColor = parseColor(bg.endColor) || '#000';
    const angle = bg.angle || '0deg';
    styles.background = `linear-gradient(${angle}, ${startColor}, ${endColor})`;
  } else if (body?.backgroundColor) {
    styles.backgroundColor = parseColor(body.backgroundColor);
  }

  // Default fallback if no background specified
  if (!styles.background && !styles.backgroundColor) {
    styles.backgroundColor = '#ffffff';
  }

  return styles;
};

// Get component styles based on type
const getBoxStyles = (box, isBodySection = false) => {
  const styles = {
    display: 'flex',
    flexDirection: box.layout === 'horizontal' ? 'row' : 'column',
  };

  if (box.spacing) styles.gap = sizeMap[box.spacing] || box.spacing;
  if (box.margin) styles.marginTop = sizeMap[box.margin] || box.margin;

  // Padding handling with default for body sections
  if (box.paddingAll) {
    styles.padding = box.paddingAll;
  } else if (
    box.paddingTop ||
    box.paddingBottom ||
    box.paddingStart ||
    box.paddingEnd
  ) {
    if (box.paddingTop) styles.paddingTop = box.paddingTop;
    if (box.paddingBottom) styles.paddingBottom = box.paddingBottom;
    if (box.paddingStart) styles.paddingLeft = box.paddingStart;
    if (box.paddingEnd) styles.paddingRight = box.paddingEnd;
  } else if (isBodySection) {
    // Default padding for body section if none specified
    styles.padding = '16px';
  }

  if (box.backgroundColor)
    styles.backgroundColor = parseColor(box.backgroundColor);
  if (box.cornerRadius) styles.borderRadius = box.cornerRadius;
  if (box.borderWidth) styles.borderWidth = box.borderWidth;
  if (box.borderColor) styles.borderColor = parseColor(box.borderColor);
  if (box.borderWidth) styles.borderStyle = 'solid';
  if (box.flex !== undefined) styles.flex = box.flex;
  if (box.justifyContent) styles.justifyContent = box.justifyContent;
  if (box.alignItems) styles.alignItems = box.alignItems;

  // Background gradient
  if (box.background?.type === 'linearGradient') {
    const bg = box.background;
    const startColor = parseColor(bg.startColor) || '#000';
    const endColor = parseColor(bg.endColor) || '#000';
    const angle = bg.angle || '0deg';
    styles.background = `linear-gradient(${angle}, ${startColor}, ${endColor})`;
  }

  return styles;
};

const getTextStyles = text => {
  const styles = {};

  if (text.size) styles.fontSize = getSize(text.size);
  if (text.color) styles.color = parseColor(text.color);
  if (text.weight) styles.fontWeight = weightMap[text.weight] || text.weight;
  if (text.align) styles.textAlign = text.align;
  if (text.flex !== undefined) styles.flex = text.flex;
  if (text.margin) styles.marginTop = sizeMap[text.margin] || text.margin;
  if (text.wrap) styles.whiteSpace = 'normal';

  return styles;
};

const getImageStyles = image => {
  const styles = {
    objectFit: image.aspectMode === 'cover' ? 'cover' : 'contain',
  };

  if (image.size && image.size !== 'full') {
    styles.width = getSize(image.size, true);
    styles.height = 'auto';
  } else {
    styles.width = '100%';
  }

  if (image.aspectRatio) {
    styles.aspectRatio = image.aspectRatio.replace(':', '/');
  }

  if (image.flex !== undefined) styles.flex = image.flex;
  if (image.margin) styles.marginTop = sizeMap[image.margin] || image.margin;
  if (image.align) {
    if (image.align === 'center') styles.margin = '0 auto';
    if (image.align === 'end') styles.marginLeft = 'auto';
  }

  return styles;
};

const getButtonStyles = button => {
  const styles = {
    padding: '10px 16px',
    borderRadius: button.style === 'primary' ? '8px' : '4px',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'center',
  };

  if (button.style === 'primary') {
    styles.backgroundColor = button.color || '#00B900';
    styles.color = '#fff';
  } else if (button.style === 'secondary') {
    styles.backgroundColor = 'transparent';
    styles.border = `1px solid ${button.color || '#00B900'}`;
    styles.color = button.color || '#00B900';
  } else {
    styles.backgroundColor = 'transparent';
    styles.color = button.color || '#00B900';
  }

  if (button.margin) styles.marginTop = sizeMap[button.margin] || button.margin;
  if (button.height) {
    styles.height =
      button.height === 'sm'
        ? '36px'
        : button.height === 'md'
          ? '44px'
          : '52px';
  }

  return styles;
};

const getSeparatorStyles = separator => {
  return {
    height: '1px',
    backgroundColor: parseColor(separator.color) || '#ddd',
    margin: `${sizeMap[separator.margin] || '8px'} 0`,
  };
};

const handleAction = action => {
  if (action?.type === 'uri' && action?.uri) {
    window.open(action.uri, '_blank');
  }
};
</script>

<script>
// Recursive component for nested Flex elements
export default {
  name: 'FlexRenderer',
};
</script>

<template>
  <div class="flex-renderer">
    <!-- Carousel: multiple bubbles side by side -->
    <div v-if="containerType === 'carousel'" class="flex-carousel">
      <div
        v-for="(bubble, bubbleIndex) in bubbles"
        :key="bubbleIndex"
        class="flex-bubble"
        :style="getBubbleStyles(bubble)"
      >
        <!-- Hero Section -->
        <div v-if="bubble.hero" class="flex-hero">
          <img
            v-if="bubble.hero.type === 'image'"
            :src="bubble.hero.url"
            :alt="bubble.hero.alt || 'Hero image'"
            :style="getImageStyles(bubble.hero)"
            class="flex-hero-image"
            @click="handleAction(bubble.hero.action)"
          />
        </div>

        <!-- Body Section -->
        <div
          v-if="bubble.body"
          class="flex-body"
          :style="getBoxStyles(bubble.body, true)"
        >
          <template v-for="(item, index) in bubble.body.contents" :key="index">
            <component
              :is="FlexComponent"
              :item="item"
              @action="handleAction"
            />
          </template>
        </div>

        <!-- Footer Section -->
        <div
          v-if="bubble.footer"
          class="flex-footer"
          :style="getBoxStyles(bubble.footer)"
        >
          <template
            v-for="(item, index) in bubble.footer.contents"
            :key="index"
          >
            <component
              :is="FlexComponent"
              :item="item"
              @action="handleAction"
            />
          </template>
        </div>
      </div>
    </div>

    <!-- Single Bubble -->
    <div v-else class="flex-bubble" :style="getBubbleStyles(flexJson)">
      <!-- Hero Section -->
      <div v-if="flexJson.hero" class="flex-hero">
        <img
          v-if="flexJson.hero.type === 'image'"
          :src="flexJson.hero.url"
          :alt="flexJson.hero.alt || 'Hero image'"
          :style="getImageStyles(flexJson.hero)"
          class="flex-hero-image"
          @click="handleAction(flexJson.hero.action)"
        />
      </div>

      <!-- Body Section -->
      <div
        v-if="flexJson.body"
        class="flex-body"
        :style="getBoxStyles(flexJson.body, true)"
      >
        <FlexComponent
          v-for="(item, index) in flexJson.body.contents"
          :key="index"
          :item="item"
          @action="handleAction"
        />
      </div>

      <!-- Footer Section -->
      <div
        v-if="flexJson.footer"
        class="flex-footer"
        :style="getBoxStyles(flexJson.footer)"
      >
        <FlexComponent
          v-for="(item, index) in flexJson.footer.contents"
          :key="index"
          :item="item"
          @action="handleAction"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.flex-renderer {
  max-width: 300px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.flex-carousel {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 8px;
}

.flex-bubble {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  min-width: 260px;
  /* Removed background: #fff to allow inline styles from Flex JSON */
}

.flex-hero {
  width: 100%;
  overflow: hidden;
}

.flex-hero-image {
  width: 100%;
  display: block;
  cursor: pointer;
}

.flex-body {
  /* Padding handled by getBoxStyles or default */
}

.flex-footer {
  padding: 12px 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}
</style>
